import * as React from 'preact/compat'
import cn from 'classnames'
import type { ComponentType } from 'preact'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { loadStylesheet, log, showModal, wait } from '../../common/helpers'
import dialogPages, { pageIndexes } from './pages'
import Api from '../../api'
import styles from '../../../css/auth.pcss.json'
import Modal from '../Modal'

interface AuthUserInfo {
  avatar: string,
  birthday: null | string,
  email: null | string,
  name: string,
  gender: 'm' | 'f',
  id: number | string,
  login: null | string,
  loginIsTaken?: boolean,
  emailIsTaken?: boolean,
  ipBlock?: boolean
}

interface StateTypes {
  shown: boolean,
  authInfo?: AuthUserInfo,
  page: string,
  transition: 'auth-transition-forward' | 'auth-transition-backward'
}

export interface DialogComponentPropTypes {
  authInfo?: AuthUserInfo,
  setAuthInfo: (info: AuthUserInfo) => void,
  switchPage: (page: string) => void
}

export type DialogComponentType = ComponentType<DialogComponentPropTypes>

class AuthDialog extends React.Component<{ onClose: () => void }, StateTypes> {
  state: StateTypes = {
    shown: false,
    page: 'index',
    transition: 'auth-transition-forward'
  }

  static instance: AuthDialog = null
  modal = React.createRef<Modal>()

  public componentDidMount() {
    AuthDialog.instance = this

    if (window.authPage) {
      const page = window.authPage
      if (!dialogPages[page]) {
        return
      }

      this.setState({
        shown: true,
        page
      })
    }
  }

  public componentWillUpdate(_nextProps: Readonly<{}>, nextState: Readonly<StateTypes>) {
    if (this.state.page !== nextState.page) {
      const transition = this.getTransitionDirection(nextState.page);
      this.setState({
        transition
      })
    }
  }

  getTransitionDirection = (nextPage: string): 'auth-transition-forward' | 'auth-transition-backward' => {
    const prevPageIndex = pageIndexes.indexOf(this.state.page)
    const nextPageIndex = pageIndexes.indexOf(nextPage)
    return prevPageIndex < nextPageIndex ? 'auth-transition-forward' : 'auth-transition-backward'
  }

  static async show() {
    if (!AuthDialog.instance) {
      // noinspection ES6MissingAwait
      loadStylesheet('auth')
      showModal(AuthDialog)
      await wait(100)
      await AuthDialog.show()
      return
    }

    window.dispatchEvent(new Event('close-all-menus'))
    if (AuthDialog.instance?.modal?.current?.id) {
      window.dispatchEvent(new CustomEvent('close-all-modals', { detail: { except: AuthDialog.instance.modal.current.id } }))
    }
    AuthDialog.instance.setState({
      shown: true
    })
  }

  static hide() {
    if (!AuthDialog.instance) {
      log('Auth', 'Auth dialog is not yet mounted')
      return
    }

    AuthDialog.instance.setState({
      shown: false,
      authInfo: undefined,
      page: 'index'
    })
  }

  switchPage = (page: string) => {
    history.replaceState('', document.title, window.location.pathname + window.location.search)
    this.setState({
      page
    })
  }

  isValidInfo = (authInfo: AuthUserInfo): [string, boolean] => {
    let page = 'index'
    let hasError = false

    if (!authInfo) {
      return [page, hasError]
    }

    if (authInfo.ipBlock) {
      page = 'ip-block'
      hasError = true
    }

    // Если в данных не хватает логина
    if (!authInfo.login) {
      page = 'get-login'
      hasError = true
    }

    // Если в данных не хватает Email
    if (!authInfo.email) {
      page = 'get-email'
      hasError = true
    }

    if (authInfo.emailIsTaken) {
      page = 'email-is-taken'
      hasError = true
    }

    return [page, hasError]
  }

  setAuthInfo = async (authInfo: AuthUserInfo) => {
    let [page, hasError] = this.isValidInfo(authInfo)

    if (!hasError) {
      await Api.post<{
        success: boolean,
        registered?: boolean,
        userInfo?: AuthUserInfo,
      }>({
        url: 'auth/add-social',
        body: authInfo
      })
        .then(result => {
          authInfo = result.userInfo
          if (result.success) {
            if (result.registered) {
              try {
                window.ym(36906115, 'reachGoal', 'register')
              } catch {
                // do nothing
              }
            }
            window.location.reload()
            return
          }
          if (!result.success) {
            page = this.isValidInfo(authInfo)[0]
          }
        })
    }

    this.setState({
      authInfo,
      page
    })
  }

  render() {
    const { shown, authInfo, page, transition } = this.state

    // «Роутинг»
    let DialogComponent: DialogComponentType = dialogPages[page] ?? dialogPages['index']

    return shown ? <Modal
      ref={this.modal}
      onClose={AuthDialog.hide}
      showClose
      mobileFullScreen
      oldModal
      disableOutsideClose
    >
      {/* @ts-ignore */}
      <TransitionGroup
        childFactory={child =>
          React.cloneElement(child, {
            classNames: transition,
            timeout: 300
          })
        }
        className={cn(styles['transition-group'], styles['sg-auth-dialog'])}
      >
        {/* @ts-ignore */}
        <CSSTransition key={page}>
          {/* @ts-ignore */}
          <div className={styles['sg-auth-dialog-inner']}>
            <DialogComponent setAuthInfo={this.setAuthInfo} authInfo={authInfo} switchPage={this.switchPage}/>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </Modal> : ''
  }
}

export default AuthDialog
